import type {
  MappedMarketsUpdates,
  MarketId,
  MarketsUpdatesHandlerRequestBody
} from '@ps/isp-web-services-types';
import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector, useStore } from 'react-redux';
import { BehaviorSubject, EMPTY, Observable, from, interval } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  scan,
  shareReplay,
  switchMap,
  takeUntil,
  withLatestFrom
} from 'rxjs/operators';

import { request } from '@swf/core/api/request';
import useUnmount from '@swf/core/hooks/useUnmount';
import { select } from '@swf/core/store';
import type { RootState } from '@swf/core/store';

import buildSportsRequest from '../utils/buildSportsRequest';
import {
  DOCUMENT_HIDDEN_MARKETS_UPDATES_INTERVAL,
  MARKETS_UPDATES_INTERVAL
} from '../utils/constants';

import type { MarketPollingData } from './../types/market';

export interface MarketPollingContextValue {
  add: (marketIds: MarketId[]) => void;
  remove: (marketIds: MarketId[]) => void;
  marketsObservable: Observable<MarketPollingData>;
}

export interface MarketRunners {
  marketId: MarketId;
  runnerIds?: string[];
}

interface ConsumerPayload {
  key: 'add' | 'remove';
  value: MarketId[];
}

export const findClosedMarkets = (
  requestIds: MarketId[],
  receivedData: MappedMarketsUpdates
): MarketId[] => requestIds.filter((id) => !receivedData[id]);

export const consumerReducer = (
  previous: MarketId[],
  payload: ConsumerPayload | null
): MarketId[] => {
  if (!payload) return previous;

  if (payload.key === 'add') {
    return previous.concat(payload.value);
  }

  const marketIds = [...previous];
  payload.value.forEach((a) => {
    const index = marketIds.findIndex((b) => a === b);

    if (index >= 0) {
      marketIds.splice(index, 1);
    }
  });

  return marketIds;
};

const fetchMarkets = (
  requestBody: MarketsUpdatesHandlerRequestBody,
  unmount: ReturnType<typeof useUnmount>,
  getState: () => RootState
): Observable<MarketPollingData> => {
  const rootState = getState();
  const sportsRequest = buildSportsRequest(request, rootState);

  return from(
    sportsRequest<MappedMarketsUpdates>({
      path: '/markets-updates?priceHistory=1' as const,
      method: 'POST' as const,
      body: requestBody
    })
  ).pipe(
    takeUntil(unmount),
    map(
      (markets): MarketPollingData => ({
        data: markets.body,
        closed: findClosedMarkets(
          requestBody.map((item) => (typeof item === 'string' ? item : item.marketId)),
          markets.body
        )
      })
    ),
    catchError(() => EMPTY)
  );
};

export const MarketPollingContext = React.createContext<MarketPollingContextValue>({
  add: () => undefined,
  remove: () => undefined,
  marketsObservable: new Observable<MarketPollingData>()
});

const MarketPollingProvider: React.FC<
  {
    value?: MarketPollingContextValue;
  } & React.PropsWithChildren
> = ({ children, ...props }) => {
  const unmount = useUnmount();

  const { getState } = useStore<RootState>();
  const isDocumentVisible = useSelector(select.documentVisibility.isDocumentVisible);
  const betSlipSelections = useSelector((rootState: RootState) =>
    select.betSlip.selections(rootState)
  );

  const consumers = useRef(new BehaviorSubject<ConsumerPayload | null>(null));
  const betSlipSubject = useRef(new BehaviorSubject<MarketRunners[]>([]));
  const pollingIntervalSubject = useMemo(
    () => new BehaviorSubject<number>(MARKETS_UPDATES_INTERVAL),
    []
  );

  useEffect(() => {
    pollingIntervalSubject.next(
      isDocumentVisible ? MARKETS_UPDATES_INTERVAL : DOCUMENT_HIDDEN_MARKETS_UPDATES_INTERVAL
    );
  }, [pollingIntervalSubject, isDocumentVisible]);

  useEffect(() => {
    const marketMap = betSlipSelections.reduce((acc, selection) => {
      const { marketId } = selection;
      const selectionId = String(selection.selectionId);

      if (acc.has(marketId)) {
        acc.get(marketId)?.push(selectionId);
      } else {
        acc.set(marketId, [selectionId]);
      }

      return acc;
    }, new Map<MarketId, string[]>());

    const betSlipMarketData = Array.from(marketMap.entries()).map(([marketId, runnerIds]) => ({
      marketId,
      runnerIds
    }));

    betSlipSubject.current.next(betSlipMarketData);
  }, [betSlipSelections]);

  const marketsObservable = useRef(
    pollingIntervalSubject.pipe(
      // Set the polling interval when the pollingIntervalSubject is changed
      switchMap((pollingInterval) => interval(pollingInterval)),
      // Stop polling if this component unmounts
      takeUntil(unmount),
      // Reduce consumers to a unique array of market IDs
      withLatestFrom(consumers.current.pipe(scan(consumerReducer, [])), betSlipSubject.current),
      // Only run if we have marketItems or bet slip selections to fetch data for
      filter(
        ([, marketItems, betSlipMarketData]) =>
          marketItems.length > 0 || betSlipMarketData.length > 0
      ),
      // Call fetchMarkets
      mergeMap(([, marketItems, betSlipMarketData]) => {
        // Extract market IDs added by add() method on useMarketPolling() hook
        // and remove duplicates
        const manualMarketIds = [...new Set(marketItems)];

        // Create request body combining manual markets and bet slip markets
        const requestBody: MarketRunners[] = [];

        // Add manual markets (without runnerIds)
        manualMarketIds.forEach((marketId) => {
          requestBody.push({ marketId });
        });

        // Add bet slip markets that are NOT already in manual markets (with runnerIds)
        betSlipMarketData.forEach((betSlipItem) => {
          if (!manualMarketIds.includes(betSlipItem.marketId)) {
            requestBody.push({
              marketId: betSlipItem.marketId,
              runnerIds: betSlipItem.runnerIds
            });
          }
        });

        // Check if requestBody contains any items with nonempty runnerIds, then send it as objects.
        // If there is no runnerIds, send it as an array of strings
        const hasRunnerIds = requestBody.some(
          (item) => item.runnerIds && item.runnerIds.length > 0
        );

        if (hasRunnerIds) {
          return fetchMarkets(requestBody, unmount, getState);
        }

        const marketIds = requestBody.map((item) => item.marketId);

        return fetchMarkets(marketIds, unmount, getState);
      }),
      // Make the observer a singleton to avoid making duplicate requests on every new subscriber
      shareReplay()
    )
  );

  const value = useMemo(
    () => ({
      add: (marketIds: MarketId[]) => {
        consumers.current.next({ key: 'add', value: marketIds });
      },
      remove: (marketIds: MarketId[]) => {
        consumers.current.next({ key: 'remove', value: marketIds });
      },
      marketsObservable: marketsObservable.current
    }),
    []
  );

  return (
    <MarketPollingContext.Provider value={props.value || value}>
      {children}
    </MarketPollingContext.Provider>
  );
};

export default MarketPollingProvider;
